import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { getEnvVar } from '@mc/client-env'
import { useBucket } from '@mc/experiments-react'
import { loadAnalytics } from '@mc/track-event'
import { TrackPageFn } from '@mc/tracking-context'
import { FB_PIXEL_OPTIONS } from './constants'

type Props = {
  analyticsLabel?: string
  analyticsIntegrations?: Record<string, boolean>
  trackPage: TrackPageFn
  fbPixelIntegrationName?: string
  shouldTrackPageLoad?: boolean
  platform?: 'next' | 'web'
}

export const Segment = ({
  shouldTrackPageLoad = true,
  platform = 'next',
  analyticsLabel,
  analyticsIntegrations,
  trackPage,
  fbPixelIntegrationName = 'Facebook Pixel',
}: Props) => {
  const isCCPA = useBucket('ccpa') === 'on'
  const isGDPR = useBucket('gdpr') === 'on'
  const shouldAutoLoadAnalytics = !isCCPA && !isGDPR

  const { asPath } = useRouter()
  const referrerRef = useRef<string | undefined>(undefined)
  const isHotJarEnabled = useBucket('enable_hotjar_legal_restrictions') === 'on'

  useEffect(() => {
    // load analytics for non ccpa/gdpr users
    if (getEnvVar('SEGMENT_ENABLED') && shouldAutoLoadAnalytics) {
      loadAnalytics(getEnvVar('SEGMENT_API_KEY') as string, {
        [fbPixelIntegrationName]: {
          dataProcessingOptions: FB_PIXEL_OPTIONS,
        },
        Hotjar: isHotJarEnabled,
      })
    }
  }, [isHotJarEnabled, shouldAutoLoadAnalytics, fbPixelIntegrationName])

  useEffect(() => {
    const referrer = referrerRef.current
    if (shouldTrackPageLoad) {
      trackPage(
        analyticsLabel,
        {
          platform,
          // Referrer from this file works for client side navigation
          ...(referrer && { referrer }),
        },
        { integrations: analyticsIntegrations },
      )
    }

    // set referrer for next route change
    referrerRef.current = window.location.href
  }, [
    analyticsIntegrations,
    analyticsLabel,
    asPath,
    trackPage,
    shouldTrackPageLoad,
    platform,
  ])

  // analytics-next is loaded via `loadAnalytics` (npm, not a CDN snippet), so
  // this component no longer injects a <script>. Loading is handled in the
  // effect above (auto-load) and by the Consent component (GDPR/CCPA paths).
  return null
}
